import { IMessageBarStyleProps, IMessageBarStyles, MessageBarType } from "@fluentui/react";

// Fluent v9's dark MessageBar look, reproduced for our v8 MessageBars.
//
// v8 renders MessageBars as borderless, mid-tone tinted rectangles (the "muddy"
// olive/amber bars). v9 instead uses a near-black, saturated fill + a 1px border
// in the intent's PRIMARY color + rounded corners, so the border and icon carry
// the intent while the fill recedes. These hexes are the resolved v9 dark tokens
// (verified against @fluentui/tokens, intent -> hue ramp -> shade):
//   background = <hue>.shade40   border = <hue>.primary   icon = <hue>.tint20/30
//   danger -> cranberry, warning -> orange, success -> green, info -> neutral.
type IntentColors = { background: string; border: string; icon: string };

const V9_DARK_INTENT: Record<"info" | "warning" | "error" | "success", IntentColors> = {
  info: { background: "#141414", border: "#666666", icon: "#adadad" }, // colorNeutralBackground3 / Stroke1 / Foreground3
  warning: { background: "#4a1e04", border: "#f7630c", icon: "#f98845" }, // orange    shade40 / primary / tint20
  error: { background: "#3b0509", border: "#c50f1f", icon: "#dc626d" }, // cranberry shade40 / primary / tint30
  success: { background: "#052505", border: "#107c10", icon: "#54b054" }, // green     shade40 / primary / tint30
};

// Map every v8 MessageBarType onto one of the four v9 intents. blocked and
// severeWarning (neither used in-app today) fold into error so, if ever used,
// they still get a border instead of falling back to the un-bordered v8 look.
const intentForType = (type: MessageBarType | undefined): keyof typeof V9_DARK_INTENT => {
  switch (type) {
    case MessageBarType.warning:
      return "warning";
    case MessageBarType.error:
    case MessageBarType.blocked:
    case MessageBarType.severeWarning:
      return "error";
    case MessageBarType.success:
      return "success";
    case MessageBarType.info:
    default:
      return "info";
  }
};

/**
 * Dark-theme MessageBar styling. Registered as `darkTheme.components.MessageBar`
 * so EVERY MessageBar picks it up with no call-site changes: Fluent's styled()
 * passes `messageBarType` through to this function (styled.js ->
 * concatStyleSetsWithProps), and any inline `styles` on a specific MessageBar
 * still layer on top of it. Light theme does not register this, so light bars
 * stay stock Fluent. Pairs with isInverted:true, which supplies the near-white
 * messageText / link that read against these dark fills.
 */
export const darkMessageBarStyles = (props: IMessageBarStyleProps): IMessageBarStyles => {
  const colors = V9_DARK_INTENT[intentForType(props.messageBarType)];
  return {
    root: {
      background: colors.background,
      border: `1px solid ${colors.border}`,
      borderRadius: 4, // Fluent v9 borderRadiusMedium
      boxSizing: "border-box",
    },
    icon: {
      color: colors.icon,
    },
  };
};
